import { initSentryReact } from '@/sentry-react';
initSentryReact();

import React from 'react';
import ReactDOM from 'react-dom/client';
import SentryErrorBoundary from '@/components/SentryErrorBoundary';
import { getAppLocale } from '@/i18n';
import type { PublicEventData, AuthUserContext, UserRegistration } from './types';
import SlotCounter from './components/SlotCounter';
import CtaButton from './components/CtaButton';
import AttendeeList from './components/AttendeeList';

const event: PublicEventData = window.__EVENT_CONTEXT__;
const authUser: AuthUserContext | null = window.__USER_CONTEXT__ ?? null;
const userRegistration: UserRegistration | null = window.__USER_REGISTRATION__ ?? null;
if (!event) {
  throw new Error('Missing __EVENT_CONTEXT__ on window');
}

const isFull = event.spots_remaining !== null && event.spots_remaining <= 0;
// Server-computed at render time (client clocks skew) — the registration API
// re-enforces this on submit, so a long-open tab degrades to a form error.
const hasStarted = !!event.has_started;

// -- Date display (server-rendered element, hydrated with locale formatting) --
const dateEl = document.getElementById('event-date-display');
const timeEl = document.getElementById('event-time-text');
if (dateEl && timeEl && event.start_at) {
  const locale = getAppLocale();
  const start = new Date(event.start_at);
  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };
  dateEl.textContent = start.toLocaleDateString(locale, dateOpts);
  let timeText = start.toLocaleTimeString(locale, timeOpts);
  if (event.end_at) {
    const end = new Date(event.end_at);
    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      timeText += ` – ${end.toLocaleTimeString(locale, timeOpts)}`;
    } else {
      timeText += ` – ${end.toLocaleDateString(locale, { ...dateOpts, ...timeOpts })}`;
    }
  }
  timeEl.textContent = timeText;
}

// -- Slot counter --
const slotRoot = document.getElementById('slot-counter-root');
if (slotRoot && !hasStarted) {
  ReactDOM.createRoot(slotRoot).render(
    <React.StrictMode>
      <SentryErrorBoundary>
        <SlotCounter
          spotsRemaining={event.spots_remaining}
          capacity={event.max_capacity}
        />
      </SentryErrorBoundary>
    </React.StrictMode>
  );
}

// -- CTA buttons --
const ctaDesktopRoot = document.getElementById('cta-desktop-root');
if (ctaDesktopRoot) {
  ReactDOM.createRoot(ctaDesktopRoot).render(
    <React.StrictMode>
      <SentryErrorBoundary>
        <CtaButton
          eventId={event.id}
          eventSlug={event.slug}
          isFull={isFull}
          hasStarted={hasStarted}
          registrationMode={event.registration_mode}
          variant="desktop"
          authUser={authUser}
          userRegistration={userRegistration}
          questions={event.questions}
          spotsRemaining={event.spots_remaining}
          price={event.price}
          currency={event.currency}
        />
      </SentryErrorBoundary>
    </React.StrictMode>
  );
}

// -- Attendee list --
const attendeeListRoot = document.getElementById('attendee-list-root');
if (attendeeListRoot) {
  ReactDOM.createRoot(attendeeListRoot).render(
    <React.StrictMode>
      <SentryErrorBoundary>
        <AttendeeList
          eventSlug={event.slug}
          visibilityMode={event.visibility_mode}
        />
      </SentryErrorBoundary>
    </React.StrictMode>
  );
}

const ctaMobileRoot = document.getElementById('cta-mobile-root');
if (ctaMobileRoot) {
  ReactDOM.createRoot(ctaMobileRoot).render(
    <React.StrictMode>
      <SentryErrorBoundary>
        <CtaButton
          eventId={event.id}
          eventSlug={event.slug}
          isFull={isFull}
          hasStarted={hasStarted}
          registrationMode={event.registration_mode}
          variant="mobile"
          authUser={authUser}
          userRegistration={userRegistration}
          questions={event.questions}
          spotsRemaining={event.spots_remaining}
          price={event.price}
          currency={event.currency}
        />
      </SentryErrorBoundary>
    </React.StrictMode>
  );
}
